import { useEffect, useState, useRef } from "react";
import { Sparkles, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * "What's new in Studio Plaz" drawer — Sprint 914.
 *
 * Auto-populated from /api/changelog/feed (parsed from PRD.md).
 * Tracks last-seen sprint via localStorage so a sparkle-dot appears
 * when there's anything new since the user's last open.
 */
export default function ChangelogDrawer({ t = (s) => s }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const drawerRef = useRef(null);
  const STORAGE_KEY = "sp.changelog.last_seen_sprint";

  // Initial fetch — see if there's anything new since last open
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/changelog/feed?limit=12");
        if (cancelled) return;
        const list = r.data?.entries || [];
        setEntries(list);
        const lastSeen = localStorage.getItem(STORAGE_KEY);
        if (list.length > 0 && list[0].sprint !== lastSeen) {
          setHasNew(true);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Click-outside-to-close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    setTimeout(() => document.addEventListener("mousedown", onClick), 100);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const handleOpen = async () => {
    setOpen(true);
    setHasNew(false);
    if (entries[0]) {
      localStorage.setItem(STORAGE_KEY, entries[0].sprint);
    }
    if (entries.length === 0) {
      setLoading(true);
      try {
        const r = await api.get("/changelog/feed?limit=12");
        setEntries(r.data?.entries || []);
      } catch { /* silent */ }
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-xl hover:bg-[var(--app-surface)] transition-colors group"
        data-testid="changelog-drawer-trigger"
        title={t("What's new in Studio Plaz")}
      >
        <Sparkles className={`w-5 h-5 transition-colors ${hasNew ? "text-amber-400 animate-pulse" : "text-[var(--app-text-muted)] group-hover:text-[var(--app-text)]"}`} />
        {hasNew && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400" data-testid="changelog-new-dot" />
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" />
          <div
            ref={drawerRef}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[440px] z-50 bg-[var(--app-bg)] border-l border-[var(--app-border)] shadow-2xl overflow-y-auto"
            data-testid="changelog-drawer"
          >
            <div className="sticky top-0 z-10 bg-[var(--app-bg)] border-b border-[var(--app-border)] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-bold text-[var(--app-text)]">
                  {t("What's new in Studio Plaz")}
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-md hover:bg-[var(--app-surface)] transition-colors"
                data-testid="changelog-drawer-close"
              >
                <X className="w-4 h-4 text-[var(--app-text-muted)]" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-5">
              {loading && (
                <div className="text-xs text-[var(--app-text-muted)]">{t("Loading…")}</div>
              )}
              {!loading && entries.length === 0 && (
                <div className="text-xs text-[var(--app-text-muted)]">{t("No updates yet.")}</div>
              )}
              {entries.map((e, i) => (
                <article key={`${e.sprint}-${i}`} className="border-l-2 border-amber-500/40 pl-4 py-1" data-testid={`changelog-entry-${i}`}>
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-[var(--app-text)] leading-snug">{e.title}</h4>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-[var(--app-text-muted)] mb-2">
                    <span className="font-mono">{e.sprint}</span>
                    {e.date && <><span>·</span><span>{e.date}</span></>}
                  </div>
                  {e.summary && (
                    <p className="text-xs text-[var(--app-text-muted)] leading-relaxed mb-2">{e.summary}</p>
                  )}
                  {e.bullets?.length > 0 && (
                    <ul className="space-y-1.5 text-xs text-[var(--app-text)]">
                      {e.bullets.map((b, bi) => (
                        <li key={bi} className="flex items-start gap-2">
                          <span className="text-amber-400 mt-1">·</span>
                          <span dangerouslySetInnerHTML={{ __html: b.replace(/`(.+?)`/g, '<code class="text-[10px] bg-[var(--app-surface)] px-1 py-0.5 rounded">$1</code>') }} />
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
